import {
  MessageBarTitle,
  Option,
  OptionOnSelectData,
} from "@fluentui/react-components";
import Dropdown from "@/components/ui/Dropdown";

import Canvas from "@/components/ui/Canvas";
import { ICanvasProps } from "@/components/ui/Canvas/Models";
import { RenderIconTime } from "@/components/ui/Icons/IconsCustom";
import { RootState } from "@/redux";
import { IOrganizationStore } from "@/redux/slices/organizations/OrganizationModel";
import {
  IDatetimeRequest,
  ISettingStore,
} from "@/redux/slices/settings/SettingModel";
import { updateDatetime } from "@/redux/slices/settings/SettingSlice";
import { IFetchRequest } from "@/services";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IMessageItem } from "../Models";
import { HeaderTitleWrapper } from "../Styles";
import { DateTimeFormatType } from "./DateTimeFormatType";
import { DatetimeSettingsProp } from "./Models";
import { DateTimeBaseWrapper } from "./Styles";
import { DateTimeFormatCollection } from "./data";
import "./main.css";
import { DateTimeFormat } from "@/services/setting-service/entity/SettingResponseModel";
import { cloneDeep } from "lodash";
import { TimeFunction } from "@/common/datetime/TimeFunction";
import { useEffect, useMemo, useState } from "react";

export const DatetimeSetting = (props: DatetimeSettingsProp) => {
  const { t } = useTranslation();

  const settingStore: ISettingStore = useSelector(
    (state: RootState) => state.settingInfo
  ) as ISettingStore;

  const [config, setConfig] = useState<DateTimeFormat>(new DateTimeFormat());

  const orgStore: IOrganizationStore = useSelector(
    (state: RootState) => state.orgInfo
  ) as IOrganizationStore;

  const [initialConfig, setInitialConfig] = useState<DateTimeFormat>(new DateTimeFormat());

  useEffect(() => {
    if (settingStore) {
      const newConfig = cloneDeep(settingStore.dateTimeFormat);
      setConfig(newConfig);
      setInitialConfig(newConfig);
    }
  }, [settingStore]);

  const dispatch = useDispatch();

  const handleSaveSettings = () => {
    if(config) {
      try {
        const OrganizationInfo: IFetchRequest = {
          tenantId: orgStore.orgInfo?.tenantId || "",
          orgId: orgStore.orgInfo?.guid || "",
        };
        const payload: IDatetimeRequest = {
          OrganizationInfo: OrganizationInfo,
          DateFormat: config.dateFormat,
          TimeFormat: config.timeFormat,
          DateSeparator: config.dateSeparator,
          FirstDayOfWeek: config.firstDayOfWeek,
        };
        dispatch(updateDatetime(payload));
  
        const msg: IMessageItem = {
          id: new Date().getTime().toString(),
          type: "success",
          content: (
            <>
              <MessageBarTitle>{t("Save config date/time successfully")}!</MessageBarTitle>
            </>
          ),
          autoClose: 3000,
        };
        props?.addMessage && props.addMessage(msg);
      } catch (error) {
        console.error("Đã xảy ra lỗi khi lưu cài đặt:", error);
        const errorMsg: IMessageItem = {
          id: new Date().getTime().toString(),
          type: "error",
          content: (
            <>
              <MessageBarTitle>Something error!</MessageBarTitle>
            </>
          ),
          autoClose: 5000,
        };
        props?.addMessage && props.addMessage(errorMsg);
      }
    }
  };

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const args: ICanvasProps = {
    headerTitle: (
      <HeaderTitleWrapper className="HeaderTitleWrapper">
        <RenderIconTime theme={settingStore.theme} />
        <span>{t("Date/Time Format")}</span>
      </HeaderTitleWrapper>
    ),
    isStickHeader: false,
    isOpen: isOpen,
    onOpen: (isOpen: boolean) => {
      setIsOpen(isOpen);
    },
  };

  const isConfigChanged = useMemo(() => {
    return JSON.stringify(config) !== JSON.stringify(initialConfig);
  }, [config, initialConfig]);

  const displayFirstDayOfWeek = DateTimeFormatCollection.FirstDayOfWeeks.find(
    (item) => item.Value === config.firstDayOfWeek?.toString()
  )?.Text || ""
  const displayTimeFormat = DateTimeFormatCollection.TimeFormates.find(
    (item) => item.Value === config.timeFormat?.toString()
  )?.Text || ""
  const displayDateFormat = DateTimeFormatCollection.DateFormates.find(
    (item) => item.Value === config.dateFormat?.toString()
  )?.Text || ""

  return (
    <DateTimeBaseWrapper className="DateTimeBaseWrapper">
      <Canvas {...args} theme={settingStore.theme || undefined}>
      <div className="rf-settings-content">
          <div className="rf-settings-content-row">
            <div className="rf-settings-content-row-title">
              {t("First day of the week")}
            </div>
            <div className="rf-settings-content-row-details">
              <Dropdown
                key={DateTimeFormatType.FirstDayOfWeeks}
                className="wapper-language"
                placeholder={t("Please select a first day")}
                size="medium"
                value={t(displayFirstDayOfWeek)}
                selectedOptions={config?.firstDayOfWeek ? [
                  config?.firstDayOfWeek,
                ] : []}
                onOptionSelect={(
                  e: { stopPropagation: () => void },
                  option: OptionOnSelectData
                ) => {
                  e.stopPropagation();
                  const configClone = cloneDeep(config);
                  configClone.firstDayOfWeek = option.optionValue || "";
                  setConfig(configClone)
                }}
              >
                {DateTimeFormatCollection?.FirstDayOfWeeks?.map((fd) => {
                  return (
                    <Option key={fd.Value} value={fd.Value}>
                      {t(fd.Text)}
                    </Option>
                  );
                })}
              </Dropdown>
            </div>
          </div>
          <div className="rf-settings-content-row">
            <div className="rf-settings-content-row-title">
              {t("Time Format")}
            </div>
            <div className="rf-settings-content-row-details">
              <Dropdown
                key={DateTimeFormatType.TimeFormates}
                className="wapper-language"
                placeholder={t("Please select a time format")}
                size="medium"
                value={`${displayTimeFormat} ( ${TimeFunction.dateTimeFormatter(new Date(), config.timeFormat)} )`}
                selectedOptions={config.timeFormat ? [
                  config.timeFormat
                ]: []}
                onOptionSelect={(
                  e: { stopPropagation: () => void },
                  option: any
                ) => {
                  e.stopPropagation();
                  const configClone = cloneDeep(config);
                  configClone.timeFormat = option.optionValue || "";
                  setConfig(configClone)
                }}
              >
                {DateTimeFormatCollection.TimeFormates?.map((tf) => {
                  return (
                    <Option key={tf.Value} value={tf.Value}>
                      {`${tf.Text} (${TimeFunction.dateTimeFormatter(new Date(), tf.Value)})`}
                    </Option>
                  );
                })}
              </Dropdown>
            </div>
          </div>
          <div className="rf-settings-content-row">
            <div className="rf-settings-content-row-title">
              {t("Date Format")}
            </div>
            <div className="rf-settings-content-row-details">
              <Dropdown
                key={DateTimeFormatType.DateFormates}
                className="wapper-language"
                placeholder={t("Please select a date format")}
                size="medium"
                value={`${displayDateFormat} ( ${TimeFunction.dateTimeFormatter(new Date(), config.dateFormat)} )`}
                selectedOptions={config.dateFormat ? [
                  config.dateFormat
                ] : []}
                onOptionSelect={(
                  e: { stopPropagation: () => void },
                  option: any
                ) => {
                  e.stopPropagation();
                  const configClone = cloneDeep(config);
                  configClone.dateFormat = option.optionValue || "";
                  setConfig(configClone)
                }}
              >
                {DateTimeFormatCollection.DateFormates?.map((t) => {
                  return (
                    <Option key={t.Value} value={t.Value}>
                      {`${t.Text} (${TimeFunction.dateTimeFormatter(new Date(), t.Value)})`}
                    </Option>
                  );
                })}
              </Dropdown>
            </div>
          </div>

          <div className="rf-settings-content-row">
            <div className="rf-settings-content-row-title">
              {t("Date Separator")}
            </div>
            <div className="rf-settings-content-row-details">
              <Dropdown
                key={DateTimeFormatType.DateSeperators}
                className="wapper-language"
                placeholder={t("Please select a date separator")}
                size="medium"
                value={DateTimeFormatCollection.DateSeperators.find(
                  (item) => item.Value === config.dateSeparator?.toString()
                )?.Text || ""}
                selectedOptions={config.dateSeparator ? [
                  config.dateSeparator
                ] : []}
                onOptionSelect={(
                  e: { stopPropagation: () => void },
                  option: any
                ) => {
                  e.stopPropagation();
                  const configClone = cloneDeep(config);
                  configClone.dateSeparator = option.optionValue || "";
                  setConfig(configClone)
                }}
              >
                {DateTimeFormatCollection.DateSeperators?.map((t) => {
                  return (
                    <Option key={t.Value} value={t.Value}>
                      {t.Text}
                    </Option>
                  );
                })}
              </Dropdown>
            </div>
          </div>

          <div className="rf-settings-content-row-footer">
            <div
              className={`rf-settings-button ${isConfigChanged ? 'active' : ''}`}
              onClick={() => isConfigChanged && handleSaveSettings()}
              title={t("Apply")}
              tabIndex={7}
              style={{
                backgroundColor: isConfigChanged ? '#0078d4' : '#b9b9b9',
                color: isConfigChanged ? 'white' : '#f1f0ef',
                cursor: isConfigChanged ? 'pointer' : 'default',
              }}
            >
              {t("Apply")}
            </div>
          </div>
        </div>
      </Canvas>
    </DateTimeBaseWrapper>
  );
};
